'use client';

import React, { useState, useMemo } from 'react';
import { Search, Download, Plus, Eye, Edit, Trash2, Copy, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, CheckCircle, Clock, FileText } from 'lucide-react';
import Link from 'next/link';
import { MOCK_EGG_PRODUCTION_RECORDS, EggProductionRecord } from '../../components/eggProductionData';
import { useRole } from '@/lib/useRole';

type SortKey = keyof EggProductionRecord;
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

export default function EggRecordsContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [houseFilter, setHouseFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('productionDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const { can } = useRole();

  const houses = useMemo(() => Array.from(new Set(MOCK_EGG_PRODUCTION_RECORDS.map(r => r.house))), []);

  const filtered = useMemo(() => {
    let data = [...MOCK_EGG_PRODUCTION_RECORDS];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        r.recordNumber.toLowerCase().includes(q) ||
        r.flockNumber.toLowerCase().includes(q) ||
        r.house.toLowerCase().includes(q) ||
        r.recordedBy.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') data = data.filter(r => r.status === statusFilter);
    if (houseFilter !== 'all') data = data.filter(r => r.house === houseFilter);
    data.sort((a, b) => {
      const av = a[sortKey] as string | number;
      const bv = b[sortKey] as string | number;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [search, statusFilter, houseFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function toggleRow(id: string) {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedRows.size === pageData.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(pageData.map(r => r.id)));
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronUp size={12} className="text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-green-600" /> : <ChevronDown size={12} className="text-green-600" />;
  }

  const statusBadge = (status: EggProductionRecord['status']) => {
    const map = {
      approved: 'bg-green-100 text-green-700',
      submitted: 'bg-blue-100 text-blue-700',
      draft: 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}>
        {status === 'approved' && <CheckCircle size={10} className="mr-1" />}
        {status === 'submitted' && <Clock size={10} className="mr-1" />}
        {status === 'draft' && <FileText size={10} className="mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production Records</h1>
          <p className="text-sm text-gray-500 mt-1">All daily egg production records across all flocks and houses.</p>
        </div>
        <div className="flex items-center gap-2">
          {can('egg_production.export') && (
            <button
              onClick={() => showToast('Exporting records to CSV...')}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Download size={15} />
              Export
            </button>
          )}
          {can('egg_production.create') && (
            <Link
              href="/egg-production/records/new"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Plus size={15} />
              New Record
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by record #, flock, house..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="submitted">Submitted</option>
            <option value="draft">Draft</option>
          </select>
          <select
            value={houseFilter}
            onChange={e => { setHouseFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Houses</option>
            {houses.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          {selectedRows.size > 0 && (
            <span className="text-xs text-gray-500 font-medium ml-auto">{selectedRows.size} selected</span>
          )}
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} records</span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selectedRows.size === pageData.length && pageData.length > 0} onChange={toggleAll} className="rounded" />
                </th>
                {[
                  { label: 'Date', key: 'productionDate' as SortKey },
                  { label: 'Record #', key: 'recordNumber' as SortKey },
                  { label: 'House', key: 'house' as SortKey },
                  { label: 'Flock', key: 'flockNumber' as SortKey },
                  { label: 'Birds', key: 'openingBirdPopulation' as SortKey },
                  { label: 'Total Eggs', key: 'totalEggs' as SortKey },
                  { label: 'Good', key: 'goodEggs' as SortKey },
                  { label: 'Cracked', key: 'crackedEggs' as SortKey },
                  { label: 'Rejected', key: 'rejectedEggs' as SortKey },
                  { label: 'Trays', key: 'fullTrays' as SortKey },
                  { label: 'HDP %', key: 'henDayProduction' as SortKey },
                  { label: 'Status', key: 'status' as SortKey },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-gray-700 select-none"
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      <SortIcon col={col.key} />
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <FileText size={20} className="text-gray-400" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600">No egg production records found</p>
                      <p className="text-xs text-gray-400">Try adjusting your filters or record a new production entry.</p>
                      {can('egg_production.create') && (
                        <Link href="/egg-production/records/new" className="mt-1 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700">
                          Record Egg Production
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ) : pageData.map((rec, i) => (
                <tr key={rec.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedRows.has(rec.id) ? 'bg-green-50' : i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selectedRows.has(rec.id)} onChange={() => toggleRow(rec.id)} className="rounded" />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{rec.productionDate}</td>
                  <td className="px-4 py-3">
                    <Link href={`/egg-production/records/${rec.id}`} className="text-xs font-semibold text-green-700 hover:underline">{rec.recordNumber}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{rec.house}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.flockNumber}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.openingBirdPopulation.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-900">{rec.totalEggs.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-green-600 font-medium">{rec.goodEggs.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-orange-500">{rec.crackedEggs}</td>
                  <td className="px-4 py-3 text-xs text-red-500">{rec.rejectedEggs}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.fullTrays}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold ${rec.henDayProduction >= (rec.targetProduction ?? 0) ? 'text-green-600' : 'text-amber-600'}`}>
                      {rec.henDayProduction}%
                    </span>
                    {rec.targetProduction && (
                      <span className="text-xs text-gray-400 ml-1">/ {rec.targetProduction}%</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{statusBadge(rec.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link href={`/egg-production/records/${rec.id}`} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="View">
                        <Eye size={14} />
                      </Link>
                      {can('egg_production.edit') && (
                        <Link href={`/egg-production/records/${rec.id}/edit`} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Edit">
                          <Edit size={14} />
                        </Link>
                      )}
                      {can('egg_production.create') && (
                        <button onClick={() => showToast(`Record ${rec.recordNumber} duplicated.`)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Duplicate">
                          <Copy size={14} />
                        </button>
                      )}
                      {can('egg_production.delete') && (
                        <button onClick={() => showToast(`Record ${rec.recordNumber} deleted.`)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => Math.abs(p - page) <= 2).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${p === page ? 'bg-green-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
